/**
 * Replace Bought Treatment Actor
 *
 * Replaces an existing bought treatment with a new one when the user
 * selects a different treatment after an appointment has already been created.
 *
 * This actor performs two operations in sequence:
 * 1. Delete the existing bought treatment(s)
 * 2. Create a new bought treatment with the new HRT
 */

import { fromPromise } from "xstate";
import { z } from "zod";
import {
  type CreateBoughtTreatmentResponse,
  createBoughtTreatment,
  deleteBoughtTreatments,
} from "../../server-actions/bought-treatment-actions";

/**
 * Input Schema (What this actor needs)
 */
const ReplaceBoughtTreatmentInputSchema = z.object({
  // IDs of bought treatments to delete
  boughtTreatmentIds: z.array(z.string()),
  // New treatment details
  consumerId: z.string(),
  pricedTreatmentId: z.number(),
  price: z.number(),
  paymentType: z.enum(["SELFPAY", "INSURANCE"]),
  insuranceIds: z.array(z.string()).optional(),
  slotId: z.string(),
  orgId: z.number(),
  treatmentName: z.string(),
  hrtId: z.number(),
  isVirtual: z.boolean().optional(),
  reviewStatus: z.string().optional(),
});

type ReplaceBoughtTreatmentInput = z.infer<
  typeof ReplaceBoughtTreatmentInputSchema
>;

/**
 * Success Schema (What this actor returns on success)
 */
const ReplaceBoughtTreatmentSuccessSchema = z.object({
  deletedIds: z.array(z.string()),
  newBoughtTreatment: z.object({
    id: z.string(),
    hrtId: z.number(),
  }),
});

type ReplaceBoughtTreatmentSuccess = z.infer<
  typeof ReplaceBoughtTreatmentSuccessSchema
>;

/**
 * Error Schema (What this actor returns on failure)
 */
const ReplaceBoughtTreatmentErrorSchema = z.object({
  code: z.enum([
    "INVALID_INPUT",
    "DELETE_FAILED",
    "CREATE_FAILED",
    "NETWORK_ERROR",
    "UNKNOWN_ERROR",
  ]),
  message: z.string(),
  retryable: z.boolean(),
  // Track which step failed for potential recovery
  failedStep: z.enum(["delete", "create"]).optional(),
});

type ReplaceBoughtTreatmentError = z.infer<
  typeof ReplaceBoughtTreatmentErrorSchema
>;

/**
 * Actor metadata
 */
export interface ActorMetadata {
  displayName: string;
  description: string;
  category: "communication" | "data" | "auth" | "unknown";
  retryable: boolean;
  timeout: number;
  errorCodes: readonly string[];
  inputSchema: z.ZodSchema;
  successSchema: z.ZodSchema;
  errorSchema: z.ZodSchema;
  onSuccess: (result: unknown) => Record<string, unknown>;
  onError: (error: unknown) => Record<string, unknown>;
  composedActors?: readonly string[];
}

/**
 * Actor with metadata wrapper
 */
export interface ActorWithMetadata {
  // biome-ignore lint/suspicious/noExplicitAny: XState actor types are too complex for static typing
  impl: any;
  metadata: ActorMetadata;
}

/**
 * Replace Bought Treatment Actor Implementation
 */
const replaceBoughtTreatmentActorImpl = fromPromise(
  async ({
    input,
  }: {
    input: ReplaceBoughtTreatmentInput;
  }): Promise<{
    deletedIds: string[];
    newBoughtTreatment: CreateBoughtTreatmentResponse;
  }> => {
    // Validate input
    const validated = ReplaceBoughtTreatmentInputSchema.safeParse(input);

    if (!validated.success) {
      const errorData: ReplaceBoughtTreatmentError = {
        code: "INVALID_INPUT",
        message: `Validation failed: ${validated.error.message}`,
        retryable: false,
      };
      const error = new Error(errorData.message);
      Object.assign(error, errorData);
      throw error;
    }

    const {
      boughtTreatmentIds,
      consumerId,
      pricedTreatmentId,
      price,
      paymentType,
      insuranceIds,
      slotId,
      orgId,
      treatmentName,
      hrtId,
      isVirtual,
      reviewStatus,
    } = validated.data;

    // Step 1: Delete existing bought treatments
    try {
      await deleteBoughtTreatments({ ids: boughtTreatmentIds });
    } catch (error) {
      const errorData: ReplaceBoughtTreatmentError = {
        code: "DELETE_FAILED",
        message: error instanceof Error ? error.message : "Delete failed",
        retryable: true,
        failedStep: "delete",
      };
      console.log("[replaceBoughtTreatmentActor] Delete failed:", errorData);
      const deleteError = new Error(errorData.message);
      Object.assign(deleteError, errorData);
      throw deleteError;
    }

    // Step 2: Create new bought treatment
    try {
      console.log(
        "[replaceBoughtTreatmentActor] Step 2: Creating new bought treatment with hrtId:",
        hrtId,
      );

      const createResult = await createBoughtTreatment({
        consumerId,
        pricedTreatmentId,
        price,
        paymentType,
        insuranceIds,
        slotId,
        orgId,
        name: treatmentName,
        source: "kiosk",
        status: "active",
        hrtId,
        isVirtual: isVirtual ?? false,
        reviewStatus,
      });

      console.log(
        "[replaceBoughtTreatmentActor] Successfully replaced bought treatment:",
        {
          deletedIds: boughtTreatmentIds,
          newBoughtTreatmentId: createResult.boughtTreatment.id,
        },
      );

      return {
        deletedIds: boughtTreatmentIds,
        newBoughtTreatment: createResult.boughtTreatment,
      };
    } catch (error) {
      const errorData: ReplaceBoughtTreatmentError = {
        code: "CREATE_FAILED",
        message: error instanceof Error ? error.message : "Create failed",
        retryable: true,
        failedStep: "create",
      };
      console.log(
        "[replaceBoughtTreatmentActor] Create failed (after successful delete):",
        errorData,
      );
      const createError = new Error(errorData.message);
      Object.assign(createError, errorData);
      throw createError;
    }
  },
);

/**
 * Replace Bought Treatment Actor with Metadata
 */
export const replaceBoughtTreatmentActor: ActorWithMetadata = {
  impl: replaceBoughtTreatmentActorImpl,
  metadata: {
    displayName: "replaceBoughtTreatment",
    description:
      "Replace existing bought treatment(s) with a new one when user changes treatment selection",
    category: "data",
    retryable: true,
    timeout: 15000, // 15 seconds
    errorCodes: [
      "INVALID_INPUT",
      "DELETE_FAILED",
      "CREATE_FAILED",
      "NETWORK_ERROR",
      "UNKNOWN_ERROR",
    ],
    inputSchema: ReplaceBoughtTreatmentInputSchema,
    successSchema: ReplaceBoughtTreatmentSuccessSchema,
    errorSchema: ReplaceBoughtTreatmentErrorSchema,
    onSuccess: (result: unknown) => {
      const success = result as ReplaceBoughtTreatmentSuccess;
      return {
        replacedBoughtTreatment: true,
        newBoughtTreatmentId: success.newBoughtTreatment.id,
        newBoughtTreatmentHrtId: success.newBoughtTreatment.hrtId,
      };
    },
    onError: (error: unknown) => {
      const err = error as ReplaceBoughtTreatmentError;
      return {
        replacedBoughtTreatment: false,
        replaceBoughtTreatmentError: {
          code: err.code,
          message: err.message,
          retryable: err.retryable,
          failedStep: err.failedStep,
        },
      };
    },
  },
};
